import { useState } from 'react';
import { Button, Flex, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useResetPassword } from '../../../api/hooks/useUserQuery';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { SignInStageEnum } from '../../../enums/SignInStage';
import { getServerErrorMsg } from '../../../helpers/errorHandler';
import { customizeRequiredMark, validationRules } from '../../../helpers/formHelper';
import { UserResetPasswordRequest } from '../../../types/user';
import ResetPasswordHeader from './ResetPasswordHeader';
import { useOTPContext } from '../../../contexts/OtpContext';

interface ActivateUserProps {
  onChangeStage: Function;
}

const ResetPassword = ({ onChangeStage }: ActivateUserProps) => {
  const resetPassword = useResetPassword();
  const { t } = useTranslation('signIn');
  const { successNotification, errorNotification } = useNotificationContext();
  const [form] = Form.useForm();
  const [isRedirectionInProgress, setIsRedirectionInProgress] = useState(false);
  const { getForgetPasswordOtpId, getEmail, getOTP } = useOTPContext();
  const email = getEmail();
  const otpId = getForgetPasswordOtpId();
  const otp = getOTP().toString();
  const onSubmitChangePassword = () => {
    const changePasswordReq: UserResetPasswordRequest = {
      email: email,
      newPassword: form.getFieldValue('newPassword'),
      otpId: otpId,
      otp: otp,
    };
    resetPassword.mutate(changePasswordReq, {
      onSuccess: () => {
        setIsRedirectionInProgress(true);
        successNotification(t('passwordChanged'));
        onChangeStage(SignInStageEnum.SIGN_IN);
      },
      onError(response) {
        let errorMsg = getServerErrorMsg(response);
        errorNotification(errorMsg);
      },
    });
  };

  const userPasswordResetForm = () => (
    <Flex className="h-full w-full" justify="center" align="center" vertical gap={8}>
      <ResetPasswordHeader />
      <Form
        form={form}
        name="reset-password"
        onFinish={onSubmitChangePassword}
        autoComplete="off"
        layout="vertical"
        className="w-full max-w-[400px]"
        validateTrigger={false}
        requiredMark={customizeRequiredMark}
      >
        <Form.Item
          label={t('password')}
          name="newPassword"
          rules={[{ required: true, validator: validationRules(t).PASSWORD }]}
          className="mb-8"
        >
          <Input.Password
            onChange={() => {
              form.validateFields(['newPassword']);
            }}
          />
        </Form.Item>

        <Form.Item
          label={t('confirmPassword')}
          name="confirmPassword"
          rules={[
            { required: true, message: t('confirmPasswordRequired') },

            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('passwordsDontMatch')));
              },
            }),
          ]}
          className="mb-8"
        >
          <Input.Password
            onChange={() => {
              form.validateFields(['confirmPassword']);
            }}
          />
        </Form.Item>

        <Form.Item>
          <Button
            className="btn-primary"
            type="primary"
            htmlType="submit"
            block
            loading={resetPassword.isPending || isRedirectionInProgress}
          >
            {t('resetPassword')}
          </Button>
        </Form.Item>
        <Form.Item>
          <p className="cursor-pointer text-center text-sm font-medium">
            {t('backTo')}{' '}
            <span
              className="text-sm font-semibold text-primary hover:underline"
              onClick={() => onChangeStage(SignInStageEnum.SIGN_IN, {})}
            >
              {t('signin')}
            </span>
          </p>
        </Form.Item>
      </Form>
    </Flex>
  );

  return (
    <Flex className="w-full" justify="center" align="center" vertical gap={8}>
      {userPasswordResetForm()}
    </Flex>
  );
};

export default ResetPassword;
